import { useTranslations } from 'next-intl';
import { JobListItemFragment } from './gen/graphql/JobListItem.generated';
import { currencySymbolForJPY, formatJPY } from '@/utils/number';
import { useAddressFormatter } from '@/hooks/useAddressFormatter';
import { FlagIcon, LocationIcon, ScheduleIcon, StoreIcon } from '@/components/icons/gen';
import Image from 'next/image';
import { useJobCategoryFormatter } from '@/hooks/useJobCategoryFormatter';
import { formatDateWithOptionalYear } from '@/utils/date';
import { useI18nLabel } from '@/hooks/useI18nLabel';
import { Tag } from '@/components/common/Tag/Tag';
import { formatMinutesToHours } from '@/utils/duration';
interface JobListItemProps {
  job: JobListItemFragment;
}

export function JobListItem({ job }: JobListItemProps) {
  const t = useTranslations('jobs.jobItem');
  const { formatAddress } = useAddressFormatter();
  const { formatJobCategory } = useJobCategoryFormatter();
  const translate = useI18nLabel();

  const {
    title,
    description,
    rangeOfHourlyWage,
    rangeOfWorkingTime,
    access,
    category,
    workplaceName,
    address,
    jobBenefits,
    mainPhotoImageURL,
    numberOfUpcomingOffers,
    closestWorkTimeStartAtOfUpcomingOffers,
  } = job;

  // Format schedule text based on rangeOfWorkingTime
  const scheduleText = rangeOfWorkingTime
    ? rangeOfWorkingTime.min === rangeOfWorkingTime.max
      ? t('workingHours', {
          num: formatMinutesToHours(rangeOfWorkingTime.min ?? 0),
        })
      : t('workingHoursWithRange', {
          min: formatMinutesToHours(rangeOfWorkingTime.min ?? 0),
          max: formatMinutesToHours(rangeOfWorkingTime.max ?? 0),
        })
    : '';

  // Format offer count for schedule text
  const offerCountText =
    numberOfUpcomingOffers && closestWorkTimeStartAtOfUpcomingOffers
      ? t('otherOffers', {
          date: formatDateWithOptionalYear(new Date(closestWorkTimeStartAtOfUpcomingOffers)),
          count: numberOfUpcomingOffers - 1,
        })
      : '';

  // Create full schedule display text
  const fullScheduleText = [scheduleText, offerCountText].filter(Boolean).join(' ');

  // Format full address
  const workplaceAddress = formatAddress(address);
  // Format category text
  const categoryText = formatJobCategory(category);

  return (
    <div className="flex flex-col gap-3 bg-white rounded-md w-full">
      {/* Header with image and text */}
      <div className="flex flex-row gap-3">
        <div className="min-w-[144px] pc:min-w-[160px] relative aspect-[3/2] rounded-small overflow-hidden">
          <Image src={mainPhotoImageURL} alt={t('altText')} className="object-cover" fill />
        </div>

        {/* Title and wage */}
        <div className="flex flex-col gap-2 flex-grow">
          {/* Job title */}
          <h3 className="font-bold text-small text-neutral-primary line-clamp-3 [overflow-wrap:anywhere]">
            {title}
          </h3>
          {/* Hourly wage range */}
          <div className="flex items-baseline text-brand-primary">
            {rangeOfHourlyWage ? (
              rangeOfHourlyWage.min === rangeOfHourlyWage.max ? (
                <div className="font-bold flex items-baseline">
                  <div className="text-style-num-x-small">{currencySymbolForJPY()}</div>
                  <div className="text-style-num-small">
                    {formatJPY(rangeOfHourlyWage?.min || 0)}
                  </div>
                </div>
              ) : (
                <>
                  <div className="font-bold flex items-baseline">
                    <div className="text-style-num-x-small">{currencySymbolForJPY()}</div>
                    <div className="text-style-num-small">
                      {formatJPY(rangeOfHourlyWage?.min || 0)}
                    </div>
                  </div>
                  <div className="font-bold text-base mx-1">{t('rangeDelimiter')}</div>
                  <div className="font-bold flex items-baseline">
                    <div className="text-style-num-x-small">{currencySymbolForJPY()}</div>
                    <div className="text-style-num-small">
                      {formatJPY(rangeOfHourlyWage?.max || 0)}
                    </div>
                  </div>
                </>
              )
            ) : (
              <span>-</span>
            )}
          </div>
        </div>
      </div>

      {/* Job Information Section */}
      <div className="flex flex-col gap-1">
        {/* Schedule Row */}
        {fullScheduleText && (
          <div className="flex items-center gap-1">
            <ScheduleIcon className="size-4 pc:size-3 text-neutral-primary flex-shrink-0" />
            <p className="text-x-small text-neutral-primary [overflow-wrap:anywhere]">
              {fullScheduleText}
            </p>
          </div>
        )}

        {/* Workplace Name Row */}
        <div className="flex items-center gap-1">
          <StoreIcon className="size-4 pc:size-3 text-neutral-primary flex-shrink-0" />
          <p className="text-x-small text-neutral-primary [overflow-wrap:anywhere]">
            {/* TODO: Configure worksplace name */}
            {workplaceName}
          </p>
        </div>

        {/* Location Row */}
        <div className="flex items-center gap-1">
          <LocationIcon className="size-4 pc:size-3 text-neutral-primary flex-shrink-0" />
          <p className="text-x-small text-neutral-primary [overflow-wrap:anywhere]">
            {workplaceAddress}
          </p>
        </div>

        {/* Access Info Row */}
        {access && (
          <div className="flex items-center gap-1">
            <FlagIcon className="size-4 pc:size-3 text-neutral-primary flex-shrink-0" />
            <p className="text-x-small text-neutral-primary [overflow-wrap:anywhere]">{access}</p>
          </div>
        )}
      </div>

      {/* Tag & Category Section */}
      <div className="flex flex-col gap-1">
        {/* Category */}
        <p className="w-full text-minute text-neutral-secondary">{categoryText}</p>

        {/* Tags/Benefits */}
        <div className="flex flex-wrap gap-1">
          {jobBenefits?.map((benefit) => (
            <Tag size="small" colorScheme="gray" key={benefit.id} label={translate(benefit)} />
          ))}
        </div>
      </div>

      {/* Description Section */}
      <div className="bg-neutral-auxiliary-tinge p-3 rounded-medium">
        <p className="text-minute text-neutral-primary line-clamp-3 [overflow-wrap:anywhere]">
          {description}
        </p>
      </div>
    </div>
  );
}
